/**
 * A picked plan made ready for the service in the browser, as the app does before it recognizes: turned upright by its
 * EXIF orientation, shrunk to `PLAN_MAX_SIDE`, flattened onto white (a transparent PNG would turn black in a JPEG) and
 * sent as a JPEG — a 12 MB phone photo leaves as a few hundred kilobytes. A small copy is the row's thumbnail.
 */
import { MAX_FILE_BYTES, PLAN_MAX_SIDE, RecognitionError, type PreparedPlan } from './protocol';

const IMAGE_TYPES = /^image\/(jpeg|png|webp|gif|bmp|avif|heic|heif)$/;
const IMAGE_NAMES = /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif)$/i;
/** Plan formats the app opens and the browser cannot draw: the service may take them later (the task's questions). */
const DOCUMENT_NAMES = /\.(pdf|dwg|dxf|tiff?)$/i;
const THUMBNAIL_SIDE = 480;
/** Smaller than this is an icon, not a plan. */
const MIN_SIDE = 64;

/** Accept for the file pickers. */
export const PLAN_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/bmp,image/avif,image/heic,image/heif,.heic,.heif';

/** What is wrong with a file before it is read, or null. */
export function planFileProblem(file: Pick<File, 'name' | 'type' | 'size'>): RecognitionError | null {
  if (DOCUMENT_NAMES.test(file.name) || file.type === 'application/pdf' || file.type === 'image/tiff') return new RecognitionError('unsupported', 'document');
  if (!IMAGE_TYPES.test(file.type) && !IMAGE_NAMES.test(file.name)) return new RecognitionError('unsupported');
  if (file.size > MAX_FILE_BYTES) return new RecognitionError('too_large');
  if (file.size === 0) return new RecognitionError('bad_image');
  return null;
}

interface Decoded { source: CanvasImageSource; width: number; height: number; close(): void }

async function decode(file: Blob): Promise<Decoded> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  } catch {
    // HEIC in Safari, and whatever else only an <img> decodes.
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    try {
      await image.decode();
    } catch {
      URL.revokeObjectURL(url);
      throw new RecognitionError('bad_image');
    }
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  }
}

function draw(source: CanvasImageSource, width: number, height: number, side: number): HTMLCanvasElement {
  const scale = Math.min(1, side / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new RecognitionError('bad_image', 'No 2D canvas');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const jpeg = (canvas: HTMLCanvasElement, quality: number) => new Promise<Blob>((resolve, reject) =>
  canvas.toBlob(blob => blob ? resolve(blob) : reject(new RecognitionError('bad_image', 'Encoding failed')), 'image/jpeg', quality));

export async function preparePlan(file: File): Promise<{ plan: PreparedPlan; thumbnail: Blob }> {
  const problem = planFileProblem(file);
  if (problem) throw problem;
  const image = await decode(file);
  try {
    if (image.width < MIN_SIDE || image.height < MIN_SIDE) throw new RecognitionError('bad_image', 'Too small');
    const full = draw(image.source, image.width, image.height, PLAN_MAX_SIDE);
    const blob = await jpeg(full, 0.92);
    const thumbnail = await jpeg(draw(full, full.width, full.height, THUMBNAIL_SIDE), 0.85);
    return { plan: { blob, name: file.name, width: full.width, height: full.height }, thumbnail };
  } finally {
    image.close();
  }
}
